import waja from 'waja';
import localSessionStorage from 'localSessionStorage';
import { error } from 'utils';
import alert from 'alert';

function checkQuery (query) {
	const dynQuery = query?.c ? query : null;
	let queryComponents = '';
	if (dynQuery) {
		for (const c of Array.isArray(dynQuery.c) ? dynQuery.c : [dynQuery.c]) {
			const comp = c.split('^');
			const queryFormat = queryComponents.length ? '&' : '?';
			queryComponents += queryFormat + comp[0] + '=' + comp[1];
		}

		localSessionStorage.set('config', JSON.stringify(dynQuery));
	} else {
		localSessionStorage.set('config', JSON.stringify(null));
	}
	return queryComponents;
};

function formatCompability (data) {
	const mappedApiData = {};

	Object.keys(data).forEach((key) => {
		const value = data[key];

		if (Array.isArray(value)) {
			// Batch compability
			mappedApiData[key] = value.map(product => ({
				productId: product.id,
			}));
		} else if (value && typeof value === 'object') {
			// Compability on config start
			const result = [];

			Object.values(value).forEach(item => {
				if (Array.isArray(item)) {
					// Product groups with multiple components
					item.forEach(id => {
						result.push({ productId: id });
					});
				} else {
					result.push({ productId: item });
				}
			});

			mappedApiData[key] = result;
		} else {
			mappedApiData[key] = value;
		}
	});

	return mappedApiData;
}

function formatCompabilityResult (type, data, configComponentList) {
	let compabilityObj = {};
	if (type === 'configuration') {
		if (data && Object.keys(data).length) {
			if (data.isValid && ! data.warnings.length) {
				compabilityObj = {
					class: '_compatible',
					message: 'Alla komponenter är kompatibla',
					shortText: 'Kompatibel',
					components: {},
				};
			} else {
				// If error, prioritize that above warning
				const topValue = data.errors.length ?
					{
						message: data.errors[0].message,
						class: '_error',
					} :
					{
						message: data.warnings[0].message,
						class: '_warning',
					};

				compabilityObj = {
					class: topValue.class,
					message: topValue.message,
					components: {},
				};

				configComponentList.forEach(element => {
					if (element?.id) {
						let item = { [element.id]: { class: '_compatible', message: 'Kompatibel' } };

						/**
						 * once again we want to prioritize errors above warnings,
						 * but we don't know which statuses each product have,
						 * so first check warnings, then if there's also an
						 * error, that will override the warning
						 */
						if (data.warnings.length) {
							for (let i = 0; i < data.warnings.length; i++) {
								const warning = data.warnings[i];
								if (warning.relatedProducts.find(prod => prod == element.id)) {
									item[element.id] = { class: '_warning', message: warning.shortMessage };
									break;
								}
							}
						}
						if (data.errors.length) {
							for (let j = 0; j < data.errors.length; j++) {
								const error = data.errors[j];
								if (error.relatedProducts.find(prod => prod == element.id)) {
									item[element.id] = { class: '_error', message: error.shortMessage };
									break;
								}

							}
						}
						compabilityObj.components = {
							...compabilityObj.components,
							...item
						};
					}
				});
			}
		}
	} else if (type === 'batch') {
		if (data.length) {
			// Batch never shows a top value, only per component
			compabilityObj = {
				class: '',
				message: '',
				components: {}
			};
			data.forEach(element => {
				if (element?.productId) {
					let item = { [element.productId]: { class: '_compatible', message: 'Kompatibel' } };

					/**
					 * In this case we know on this level the length
					 * of both errors and warnings, so we check which
					 * array we should loop. And again, we prioritize
					 * errors over warnings
					 */
					const relevantArray = element.errors.length ? element.errors : element.warnings;

					for (let i = 0; i < relevantArray.length; i++) {
						const status = relevantArray[i];
						const classStatus = status.severity === 'Error' ? '_error' : '_warning';
						item[element.productId] = { class: classStatus, message: status.shortMessage };
						break;
					}
					compabilityObj.components = {
						...compabilityObj.components,
						...item
					};
				}
			});

			/** And now after checking errors and warnings,
			 * we also add all the components without issues to
			 * the compability object
			 */
			configComponentList.forEach(element => {
				if (element?.id) {
					/** Check if component is already added, if it is
					 * it has an error or warning
					 */
					if (! compabilityObj.components?.[element.id]) {
						const item = { [element.id]: { class: '_compatible', message: 'Kompatibel' } };
						compabilityObj.components = {
							...compabilityObj.components,
							...item
						};
					}
				}
			});

		} else {
			compabilityObj = {
				class: '_compatible',
				message: 'Alla komponenter är kompatibla',
				shortText: 'Kompatibel',
				components: {}
			};
		}
	}
	return compabilityObj;
}

export default {
	getConfigComponentList ({ state, commit, dispatch }, { productId, query }) {
		// If we are getting config, we probably need to reset compability
		commit('setCompability', 'reset');
		commit('setConfigError', null);
		const formattedQuery = query ? checkQuery(query) : '';

		waja.get('config/' + productId + formattedQuery)
			.on('success', res => {
				let exchanges = {};
				const configComponentList = res.data.componentGroups.map(item => {
					if (item.product?.id && item.product.id !== item.defaultProductId) {
						exchanges = {
							...exchanges,
							[item.id]: item.defaultProductId
						}
					}
					return {
						groupName: item.name,
						groupId: item.id,
						icon: item.icon,
						stock: item.stock,
						hidden: item.hidden,
						...item.product
					};
				});
				// Kanske inte måste sätta om basprodukten??
				commit('setExchanges', exchanges);
				commit('setConfigComponentList', configComponentList);
				commit('setMainProduct', res.data);
			})
			.on('error', res => { //behöver vi göra detta för group också?
				res.json().then(json => {
					alert(json.error);
					commit('setConfigError', { heading: 'Error >.<', subText: json.error});
				});
			}).go();
	},
	getGroupData ({ state, commit, dispatch }, { configId, groupId, componentId }) {
		waja.get('config/' + configId + '/' + groupId + '/' + componentId)
			.on('success', res => {
				commit('setGroupData', res.data);
				commit('setDefaultProductId', res.data.defaultProductId);
				let urlList = null;

				for (let i = 0; i < res.data.products.length; i++) {
					if (i) {
						urlList += ',';
					}
					urlList += res.data.products[i].id;
				}

				commit('setUrlList', urlList);
				if (! state.mainProduct) {
					const query = localSessionStorage.get('config') ? JSON.parse(localSessionStorage.get('config')) : null;
					dispatch('getConfigComponentList', { productId: configId, query });
				}
			})
			.on('error', () => {
				error(404);
			}).go();
	},
	getCompatibilityConfiguration({ commit, state }, components) {
		console.log('********', components);
		const data = formatCompability({ products: components });
		waja.post('config/compatibility/configuration')
			.data(data)
			.on('success', res => {
				const formattedCompability = formatCompabilityResult('configuration', res.data, state.configComponentList);
				commit('setCompability', formattedCompability);
			})
			.on('error', () => {
				error(404);
			}).go();
	},
	getCompatibilityBatch({ commit, state }, data) {
		const dataToFormat = {
			baseConfiguration: data.baseConfiguration,
			productsToValidate: data.productsToValidate,
			productIdToReplace: data.productIdToReplace,
		};
		const batchData = formatCompability(dataToFormat);
		waja.post('config/compatibility/batch')
			.data(batchData)
			.on('success', res => {
				const formattedCompability = formatCompabilityResult('batch', res.data, [...state.groupData.products, state.groupData.currentProduct]);
				commit('setCompability', formattedCompability);
			})
			.go();
	},
	/** To avoid old data showing in different groups, we want to
	 *  reset every time we leave the "selected-component" page
	 */
	resetSelectedComponentStates ({ commit }) {
		commit('setGroupData', null);
		commit('setDefaultProductId', null);
		commit('setUrlList', null);
	},
	/** To avoid old data showing on product pages
	 */
	resetConfigBaseData({ commit }) {
		commit('setMainProduct', null);
		commit('setConfigComponentList', []);
	},
};